/* eslint-disable react/prop-types */
import React, { useState } from "react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import useOrderHistory from "../../../../hooks/fetch-hooks/order-history/use-order-history";
import { buildTimeline, EMPTY_VALUE } from "./buildTimeline";

// "27 Oct, 02:44 PM" -- the year only when it isn't this one.
const formatWhen = (value) => {
    const when = moment(value);
    return when.format(when.isSame(moment(), "year") ? "DD MMM, hh:mm A" : "DD MMM YYYY, hh:mm A");
};

// On narrow screens a row becomes a small card; these labels (hidden from `sm:` up) stand in
// for the column headings.
function CellLabel({ children }) {
    return <span className="mr-2 text-xs text-gray-500 sm:hidden">{children}</span>;
}

function ValueText({ value, isNew = false }) {
    const isEmpty = value === EMPTY_VALUE;
    const tone = isNew ? "font-medium text-gray-800" : "text-gray-500";
    return <span className={`break-words ${isEmpty ? "text-gray-400" : tone}`}>{value}</span>;
}

// One <tbody> per entry. An edit that touched several fields gets one row per field, with
// when/who shown on the first row only.
function EntryRows({ entry }) {
    const when = moment(entry.occurredAt);
    return (
        <tbody className="block border-t border-gray-100 py-2 sm:table-row-group sm:py-0">
            {entry.changes.map((change, index) => {
                const isFirstRow = index === 0;
                return (
                    <tr key={change.label} className="block sm:table-row">
                        <td className={`px-3 align-top sm:table-cell sm:whitespace-nowrap sm:py-3 ${isFirstRow ? "block" : "hidden"}`}>
                            {isFirstRow && (
                                <time dateTime={when.toISOString()} title={when.format("DD MMM YYYY, hh:mm:ss A")} className="text-sm text-gray-600">
                                    {formatWhen(entry.occurredAt)}
                                </time>
                            )}
                        </td>
                        <td className={`px-3 align-top sm:table-cell sm:py-3 ${isFirstRow ? "block" : "hidden"}`}>
                            {isFirstRow && (
                                <span className="inline-flex items-center gap-2 text-sm text-gray-800">
                                    <span
                                        aria-hidden="true"
                                        title={entry.isByPerson ? "Made by a person" : "Automatic"}
                                        className={`h-2 w-2 flex-shrink-0 rounded-full ${entry.isByPerson ? "bg-[#069079]" : "border-2 border-gray-400 bg-white"}`}
                                    />
                                    {entry.actorLabel}
                                </span>
                            )}
                        </td>
                        <td className="block px-3 align-top text-sm font-medium text-gray-800 sm:table-cell sm:py-3">
                            {change.label}
                        </td>
                        <td className="block px-3 align-top text-sm sm:table-cell sm:py-3">
                            <CellLabel>Before</CellLabel>
                            <ValueText value={change.from} />
                        </td>
                        <td className="block px-3 align-top text-sm sm:table-cell sm:py-3">
                            <CellLabel>After</CellLabel>
                            <ValueText value={change.to} isNew />
                        </td>
                    </tr>
                );
            })}
        </tbody>
    );
}

function LedgerTable({ timeline }) {
    return (
        <div className="overflow-x-auto">
            <table className="block w-full border-collapse sm:table sm:table-fixed">
                <colgroup className="hidden sm:table-column-group">
                    <col className="w-40" />
                    <col className="w-48" />
                    <col className="w-40" />
                    <col />
                    <col />
                </colgroup>
                <thead className="hidden sm:table-header-group">
                    <tr className="h-10 border-b-2 border-gray-100 text-left text-sm font-semibold text-gray-700">
                        <th className="px-3 font-semibold">When</th>
                        <th className="px-3 font-semibold">Who</th>
                        <th className="px-3 font-semibold">Change</th>
                        <th className="px-3 font-semibold">Before</th>
                        <th className="px-3 font-semibold">After</th>
                    </tr>
                </thead>
                {timeline.map((entry) => <EntryRows key={entry.key} entry={entry} />)}
            </table>
        </div>
    );
}

// Mounted only while the section is open, so the history is fetched on first open and
// re-fetched on every re-open. Edits are saved by a queue worker (drains every 30s by
// default), so a change made just now may not be here yet -- hence the note and Refresh.
function OrderHistoryBody({ orderId }) {
    const { data, isLoading, isFetching, isError, refetch } = useOrderHistory(orderId);

    if (isLoading) return <p className="py-4 text-sm text-gray-500">Loading history...</p>;

    if (isError) {
        return (
            <div className="flex items-center gap-3 py-4">
                <p className="text-sm text-red-600">Couldn&apos;t load the order history.</p>
                <button type="button" onClick={() => refetch()} className="text-sm font-medium text-gray-800 underline">
                    Try again
                </button>
            </div>
        );
    }

    const timeline = buildTimeline(data?.data?.data);

    return (
        <div>
            <div className="mb-2 flex items-center justify-between gap-4">
                <p className="text-xs text-gray-500">Edits can take up to 30 seconds to appear.</p>
                <button
                    type="button"
                    onClick={() => refetch()}
                    disabled={isFetching}
                    className="text-sm font-medium text-gray-800 underline disabled:text-gray-400 disabled:no-underline"
                >
                    {isFetching ? "Refreshing..." : "Refresh"}
                </button>
            </div>
            {timeline.length === 0
                ? <p className="py-4 text-sm text-gray-500">No changes recorded for this order yet.</p>
                : <LedgerTable timeline={timeline} />}
        </div>
    );
}

export default function OrderHistory({ orderId }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const panelId = `order-history-${orderId}`;

    return (
        <section className="bg-white rounded-2xl shadow-soft-xl">
            <button
                type="button"
                aria-expanded={isExpanded}
                aria-controls={panelId}
                onClick={() => setIsExpanded((expanded) => !expanded)}
                className="w-full flex items-center justify-between gap-3 p-6 text-left rounded-2xl hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#069079]"
            >
                <span>
                    <span className="block text-lg font-semibold">Order History</span>
                    <span className="block text-xs text-gray-500">Status changes and edits made by the team</span>
                </span>
                <FontAwesomeIcon
                    icon={faChevronDown}
                    aria-hidden="true"
                    className={`text-gray-500 transition-transform motion-reduce:transition-none ${isExpanded ? "rotate-180" : ""}`}
                />
            </button>
            {isExpanded && (
                <div id={panelId} className="px-6 pb-6">
                    <OrderHistoryBody orderId={orderId} />
                </div>
            )}
        </section>
    );
}
